import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import type { ConfirmFn } from '../seams';

// Once-only, non-blocking nudge to register Profile.bodyweightKg the first
// time a bodyweight set is completed in a LIVE logger session while the
// weight is unset (D1-S2, 2026-05-29). Bodyweight volume + the bw-inclusive
// totals (D1-S1) can't be computed without it.
//
// Extracted from useLoggerScreen (which is already at the size budget). The
// guard ref resets per session: the Modal keeps the host hook mounted across
// workouts, so without a reset only the first workout of the app run would
// ever prompt. `sessionKey` (draft.startedAt) changes exactly once per
// session (initial / hydrate / reset) — the same signal the rest timer uses.
//
// Returns a callback the screen fires from the set-completion path; the
// caller decides WHEN (live bodyweight set done), this decides WHETHER.
//
// ⛔ Through the confirm SEAM, not react-native's Alert (RW-25) — inside
// packages/ui an Alert never fires on react-native-web, so on the web this
// nudge simply never appeared. It is the one of the nine that is NOT
// destructive: "Set it" is the affirmative and "Later" the way out, so the
// seam's confirm/cancel pair reads as invite/dismiss here.
export function useBodyweightPrompt(args: {
  bodyweightKg: number | null | undefined;
  sessionKey: string;
  confirm: ConfirmFn;
  onRequestSetBodyweight?: () => void;
}) {
  const { bodyweightKg, sessionKey, confirm, onRequestSetBodyweight } = args;
  const { t } = useTranslation();
  const shownRef = useRef(false);
  useEffect(() => {
    shownRef.current = false;
  }, [sessionKey]);
  return useCallback(() => {
    if (shownRef.current) return;
    // Truthy ⇒ a real weigh-in (schema min 20kg); 0/null/undefined ⇒ unset.
    if (bodyweightKg) return;
    shownRef.current = true;
    void confirm({
      title: t('mobile.logger.bwPromptTitle', {
        defaultValue: 'Register your bodyweight',
      }),
      body: t('mobile.logger.bwPromptBody', {
        defaultValue:
          'Bodyweight exercises need your weight to compute load and volume. Set it in Settings → Body.',
      }),
      confirmLabel: t('mobile.logger.bwPromptCta', { defaultValue: 'Set it' }),
      cancelLabel: t('workout.cancel', { defaultValue: 'Later' }),
      // The one non-destructive caller of the seam: "Set it" opens Settings →
      // Body, it does not throw anything away. Without this it inherits the
      // red reserved for Delete/Discard.
      destructive: false,
    }).then((ok) => {
      if (ok) onRequestSetBodyweight?.();
    });
  }, [bodyweightKg, confirm, onRequestSetBodyweight, t]);
}
